// LoanOfferNew.tsx
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { AlertDialog, AlertDialogAction, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Info } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "@/components/ui/use-toast";
import axios from '@/lib/axios';
import { API_BASE_URL } from '@/config';
import DataTable from "react-data-table-component";
import { useLoader } from "../contexts/LoaderContext";
import CallbackHelp from "@/components/CallbackHelp";
import JourneyHeader from "@/components/JourneyHeader";

// Initialize Time Constants for the loan calculator [load min max from backend via views.set** func]
const MIN_LOAN_AMOUNT = 0;
const MAX_LOAN_AMOUNT = 1000;
const TENURE_OPTIONS = [
  { months: 6, label: "6 months", feePercentage: 10 }
];
const MAX_DAILY_REVENUE_PERCENTAGE = 40;

const LoanOffer = () => {
  const navigate = useNavigate();

  // `loanAmount` = the value currently shown in the slider / header (updates live while dragging)
  const [loanAmount, setLoanAmount] = useState<number>(MAX_LOAN_AMOUNT);

  // `syncedLoanAmount` = the amount that the last successful API call was made for.
  // When loanAmount !== syncedLoanAmount, the derived data below is stale → blur it.
  const [syncedLoanAmount, setSyncedLoanAmount] = useState<number>(MAX_LOAN_AMOUNT);

  const [amountYouGet, setAmountYouGet] = useState<number>(0.5 * MAX_LOAN_AMOUNT);
  const [amountYouPay, setAmountYouPay] = useState<number>(2 * MAX_LOAN_AMOUNT);
  const [minLoanAmount, setMinLoanAmount] = useState<number>(MIN_LOAN_AMOUNT);
  const [maxLoanAmount, setMaxLoanAmount] = useState<number>(MAX_LOAN_AMOUNT);
  const [dailyRepayment, setDailyRepayment] = useState<number>(MAX_LOAN_AMOUNT);
  const [procFeePerc, setProcFeePerc] = useState<number>(MAX_LOAN_AMOUNT);
  const [deduction, setDeduction] = useState<number>(0);
  const [deductionText, setDeductionText] = useState<string>("");
  const [feeGstAmount, setFeeGstAmount] = useState<number>(0);
  const [insuranceFeeAmount, setInsuranceFeeAmount] = useState<number>(0);
  const [nachFee, setNachFee] = useState<number>(0);
  const [selectedTenure, setSelectedTenure] = useState<number>(6);
  const [repaymentMethod, setRepaymentMethod] = useState<'fixed' | 'percentage'>('fixed');
  const [isCallbackOpen, setIsCallbackOpen] = useState(false);
  const { loading, setLoading, setLoaderMessage } = useLoader();
  const [tableData, setTableData] = useState([]);
  const [hideMe, setHideMe] = useState(false);
  const [isDummyData, setIsDummyData] = useState(true);
  const [isFormSynced, setIsFormSynced] = useState(false);
  const [lendingEntity, setLendingEntity] = useState(false);
  const [lendingEntityBusinessName, setLendingEntityBusinessName] = useState("");
  const [open, setOpen] = useState(false);
  const [repayType, setRepayType] = useState<string>("EMI");

  // Async BRE polling refs
  const brePollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const brePolling = useRef(false);

  // AbortController ref to cancel in-flight repayment schedule requests
  // when a newer Calculate press arrives before the previous response returns.
  const scheduleAbortRef = useRef<AbortController | null>(null);

  // Cleanup polling and any in-flight schedule requests on unmount
  useEffect(() => {
    return () => {
      if (brePollRef.current) clearInterval(brePollRef.current);
      if (scheduleAbortRef.current) scheduleAbortRef.current.abort();
    };
  }, []);

  const handleToggle = () => {
    // Toggle on mobile (touch)
    if (window.matchMedia("(hover: none)").matches) setOpen((prev) => !prev);
  };

  // True when the slider has been moved but "Calculate" hasn't been pressed yet.
  // In this state all derived data sections are blurred to signal they are stale.
  const isDataStale = loanAmount !== syncedLoanAmount;

  // helper: push form data to backend
  // Cancels any previous in-flight request so only the latest value wins.
  const getLoanSummaryAndSchedule = async (values: {
    loanAmount: number;
    selectedTenure: number;
    repaymentMethod: 'fixed' | 'percentage';
  }) => {
    // Cancel any previous in-flight schedule request
    if (scheduleAbortRef.current) {
      scheduleAbortRef.current.abort();
    }
    const abortController = new AbortController();
    scheduleAbortRef.current = abortController;

    try {
      setIsFormSynced(false);
      console.log("V", values);
      const res = await axios.post(`${API_BASE_URL}/api/repayment_schedule/`, values, {
        signal: abortController.signal,
      });
      console.log("after repayment schedule hit", res.data);

      // Response from your backend
      const { summary, schedule } = res.data;
      console.log('---------------SUMMARY:', summary);
      // Transform schedule into DataTable-friendly format
      const formatted = Array.isArray(schedule)
        ? schedule.map((row: any) => ({
          day: row.EDI_Day,
          date: row.EDI_Date,
          amount: row.EDI_Repayments,
        }))
        : [];

      setAmountYouGet(summary.net_disbursal);
      setProcFeePerc(summary.proc_fee_pct);
      setDeduction(summary.deduction);
      setDeductionText(summary.deduction_text);
      setFeeGstAmount(summary.gst_on_proc_amt || 0);
      setInsuranceFeeAmount(summary.insurance_fee_amt || 0);
      setNachFee(summary.nach_fee || 0);
      setAmountYouPay(summary.offer_amount_winterest);
      setDailyRepayment(summary.epi);
      setLendingEntity(summary.lender);
      setLendingEntityBusinessName(summary.lender_full_name);

      console.log("formatted data", formatted);
      setTableData(formatted);

      // Mark this amount as synced — removes the blur
      setSyncedLoanAmount(values.loanAmount);

      setTimeout(() => {
        setIsFormSynced(true);
      }, 100);
    } catch (err: any) {
      // Ignore intentional cancellations (AbortController.abort() was called
      // because a newer Calculate press arrived — this is not a real error).
      if (err?.name === 'AbortError' || err?.code === 'ERR_CANCELED') {
        console.log('[LoanOffer] Repayment schedule request cancelled (superseded by newer value)');
        return;
      }
      console.error("Error syncing with backend:", err);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update repayment schedule.",
      });
      setIsFormSynced(true); // Ensure loader stops even on error
    }
  };

  // Called when the user clicks the "Calculate" button after moving the slider.
  const handleCalculate = () => {
    getLoanSummaryAndSchedule({
      loanAmount,
      selectedTenure,
      repaymentMethod,
    });
  };

  // Calculate the fee percentage based on selected tenure
  const getFeePercentage = () => {
    const tenure = dynamicTenureOptions.find(option => option.months === selectedTenure);
    return tenure ? tenure.feePercentage : 10;
  };

  // Calculate fee amount based on loan amount and fee percentage
  const calculateFeeAmount = () => {
    return (loanAmount * getFeePercentage()) / 100;
  };

  // Calculate the daily fixed payment
  const calculateDailyPayment = () => {
    return dailyRepayment;
  };

  // Calculate the daily revenue percentage
  const calculateDailyRevenuePercentage = () => {
    const percentage = (loanAmount / MAX_LOAN_AMOUNT) * MAX_DAILY_REVENUE_PERCENTAGE;
    return Math.min(Math.max(percentage, 5), MAX_DAILY_REVENUE_PERCENTAGE);
  };

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const finishBreWithNavigation = (data: any) => {
    brePolling.current = false;
    setLoaderMessage(null); // clear BRE message
    setLoading(false);
    navigate(data.next_page_url, {
      state: {
        message: data.message,
        amount_updated: data.amount_updated,
        new_eligible_amount: data.new_eligible_amount,
        initial_loan_amount: data.initial_loan_amount,
      },
    });
  };

  const pollBreStatus = () => {
    if (brePollRef.current) clearInterval(brePollRef.current);
    brePollRef.current = setInterval(async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/check_bre_status/`);
        const data = res.data;

        if (data.status === 'BRE_ERROR') {
          clearInterval(brePollRef.current!);
          brePolling.current = false;
          setLoaderMessage(null); // clear BRE message
          setLoading(false);
          toast({
            variant: 'destructive',
            title: 'Lender API Error',
            description: data.message || 'Lender API is temporarily unavailable. Please try again.',
          });
        } else if (data.status && data.status !== 'BRE_PROCESSING' && data.next_page_url) {
          clearInterval(brePollRef.current!);
          finishBreWithNavigation(data);
        }
        // BRE_PROCESSING → keep polling
      } catch (err) {
        console.error('Error polling BRE status:', err);
      }
    }, 3000);
  };

  const handleNext = async () => {
    setLoading(true);
    setLoaderMessage("bre"); // trigger BRE animated loader
    const data = {
      loanAmount: loanAmount,
      selectedTenure: selectedTenure,
      feePercentage: getFeePercentage(),
      feeAmount: calculateFeeAmount(),
      feeGstAmount: feeGstAmount,
      insuranceFeeAmount: insuranceFeeAmount,
      nachFee: nachFee,
      repaymentValue: repaymentMethod == "fixed" ? calculateDailyPayment() : calculateDailyRevenuePercentage().toFixed(1),
      repaymentMethod: repaymentMethod,
      lendingEntity: lendingEntity,
    };

    try {
      // 1. Save loan offer details
      const res = await axios.post(`${API_BASE_URL}/api/set_loan_offer_details/`, data, { maxRedirects: 0 });
      console.log("Set loan offer details response:", res.data);

      if (res.request?.responseURL?.includes("https://apply.intellendtech.com/accounts/login/")) {
        navigate("/session-timeout");
        return;
      }

      // 2. Queue BRE evaluation — returns 202 immediately; poll check_bre_status
      // for the real outcome. The old synchronous await caused the gateway to
      // time out after 30s, which the frontend mistakenly treated as a rejection.
      try {
        const breRes = await axios.post(`${API_BASE_URL}/api/evaluate_lender_bre/`);
        console.log("BRE evaluation queued:", breRes.data);

        if (breRes.data.success && breRes.data.queued) {
          // 202 queued — start polling
          brePolling.current = true;
          pollBreStatus();
        }
      } catch (breErr: any) {
        console.error("BRE evaluation error:", breErr);
        const status = breErr.response?.status;
        const errorMsg = breErr.response?.data?.message || "Internal evaluation error";
        const errorCode = breErr.response?.data?.error_code;

        // CIBIL file not configured — show toast and stay on page
        if (status === 400 && errorCode === "CIBIL_FILE_MISSING") {
          toast({
            variant: "destructive",
            title: "Configuration Error",
            description: errorMsg,
          });
          return;
        }

        // Lender API unavailable — show toast and stay on page
        if (status === 500 || status === 503) {
          toast({
            variant: "destructive",
            title: "Lender API Error",
            description: errorMsg,
          });
          return;
        }

        // Previous evaluation still running — poll for its result
        if (status === 429 && breErr.response?.data?.in_progress) {
          brePolling.current = true;
          pollBreStatus();
        } else {
          toast({
            variant: 'destructive',
            title: 'Evaluation Error',
            description: errorMsg,
          });
        }
      }

    } catch (err: any) {
      console.error("Error saving data:", err);
      if (confirm("Error saving data: " + err.message + ".\n Press OK to reload the page.")) {
        window.location.reload();
      }
    } finally {
      if (!brePolling.current) {
        setLoaderMessage(null);
        setLoading(false);
      }
    }
  };


  useEffect(() => {
    if ((!isDummyData) && isFormSynced) {
      setLoading(false);
    }
    else {
      setLoading(true);
    }
  },
    [isDummyData, isFormSynced]);


  useEffect(() => {
    // Expecting this to only trigger once, after page load when
    // we load the first data from views **
    if (isDummyData) {
      return;
    }
    getLoanSummaryAndSchedule({
      loanAmount,
      selectedTenure,
      repaymentMethod,
    });
  }, [isDummyData]);

  const [lenderName, setLenderName] = useState<string>("Bank");
  const [lenderLogo, setLenderLogo] = useState<string | null>(null);
  const [offerId, setOfferId] = useState<number | null>(null);
  const [dynamicTenureOptions, setDynamicTenureOptions] = useState(TENURE_OPTIONS);
  const [fetchError, setFetchError] = useState(false);

  // Update fetch logic
  useEffect(() => {
    setLoading(true);
    axios
      .post(`${API_BASE_URL}/api/get_loan_offer_details/`, {}, { maxRedirects: 0 })
      .then((res) => {
        console.log("Backend response:", res.data);
        if (res.request.responseURL.includes("https://apply.intellendtech.com/accounts/login/")) {
          navigate("/session-timeout");
        }
        if (res.status === 200) {
          if (res.data['min_loan_amount']) {
            setMinLoanAmount(res.data['min_loan_amount']);
          }
          if (res.data['max_loan_amount']) {
            setMaxLoanAmount(res.data['max_loan_amount']);
          }
          if (res.data['loan_amount']) {
            setLoanAmount(res.data['loan_amount']);
            setSyncedLoanAmount(res.data['loan_amount']); // initial load is already synced
          }
          if (res.data['lender_name']) {
            setLenderName(res.data['lender_name']);
          }
          if (res.data['lender_logo']) {
            setLenderLogo(res.data['lender_logo']);
          }

          // Handle Tenure
          if (res.data['loan_tenure']) {
            setSelectedTenure(res.data['loan_tenure']);
            // Update options to include this tenure if not present
            setDynamicTenureOptions([{
              months: res.data['loan_tenure'],
              label: `${res.data['loan_tenure']} ${res.data['repay_type'] === 'EDI' ? 'days' : 'months'}`,
              feePercentage: 10 // Default or fetch?
            }]);
          }

          if (res.data['repayment_method']) {
            setRepaymentMethod(res.data['repayment_method']);
          }

          if (res.data['repay_type']) {
            setRepayType(res.data['repay_type']);
          }

          if (res.data['offer_id']) {
            setOfferId(res.data['offer_id']);
          }
          setTimeout(() => { setIsDummyData(false); }, 100);
        }
      })
      .catch((err) => {
        console.error("Fetch error:", err);
        setFetchError(true);
        setLoading(false); // Force stop loading on fatal error
      })
      .finally(() => {
      });
  }, []);

  // Modify logic to not force loading if there is a fetch error
  useEffect(() => {
    if (fetchError) {
      setLoading(false);
      return;
    }
    if ((!isDummyData) && isFormSynced) {
      setLoading(false);
    }
    else {
      setLoading(true);
    }
  },
    [isDummyData, isFormSynced, fetchError]);

  return (
    <div className="min-h-screen bg-intellend-white">
      {/* Header with logo */}
      <JourneyHeader />

      {/* Main content */}
      <main className="px-3 py-2 max-w-3xl mx-auto">
        <div className="bg-white shadow-md rounded-lg p-4">
          <h2 className="text-xl font-bold text-intellend-green mb-3">Loan Offer from<br />
            {lenderLogo && <img src={lenderLogo} alt={lenderName} className="h-8 mb-2 object-contain" />}
            <span className="text-intellend-green/70">{lenderName}</span></h2>

          {/* Loan Amount Slider */}
          <div className="mb-3">
            <div className="flex justify-between items-center mb-1">
              <h3 className="font-semibold">Loan Amount [Principal]</h3>
              <div className="font-bold">
                {formatCurrency(loanAmount)}
              </div>
            </div>
            <Slider
              defaultValue={[maxLoanAmount]}
              min={minLoanAmount}
              max={maxLoanAmount}
              step={1000}
              onValueChange={(values) => {
                // Only update the displayed amount while dragging.
                // The API is NOT called here — user must press "Calculate".
                setLoanAmount(values[0]);
              }}
              // onValueCommit intentionally removed — "Calculate" button drives API calls.
              className="mb-1"
              value={[loanAmount]}
            />
            <div className="flex justify-between text-xs text-gray-500 mb-2">
              <span>{formatCurrency(minLoanAmount)}</span>
              <span>{formatCurrency(maxLoanAmount)}</span>
            </div>

            {/* Calculate button — shown whenever the slider value differs from the last synced value */}
            {isDataStale && (
              <div className="flex justify-center mt-2 mb-1">
                <Button
                  onClick={handleCalculate}
                  disabled={!isFormSynced && !isDataStale}
                  className="w-full bg-intellend-green hover:bg-intellend-green/90 text-sm py-2"
                >
                  Calculate for {formatCurrency(loanAmount)}
                </Button>
              </div>
            )}
          </div>

          {/* ── All sections below are blurred when the slider has been moved
               but "Calculate" hasn't been pressed yet.                       ── */}
          <div className={cn(
            "transition-all duration-200",
            isDataStale ? "blur-sm pointer-events-none select-none opacity-60" : ""
          )}>
            {/* Amount You Get */}
            <div className="mb-3 p-2 bg-green-50 rounded-lg">
              <div className="flex justify-between">
                <div className="flex flex-col">
                  <h3 className="font-semibold mr-1"> Amount You Get </h3>
                  <div className='text-xs text-gray-500'>After deducting {deductionText}</div>
                </div>
                <div className="text-right">
                  <div className="font-bold">
                    {formatCurrency(amountYouGet)}
                  </div>
                </div>
              </div>
            </div>

            {/* Amount You Pay */}
            <div className="mb-3 p-2 bg-red-50 rounded-lg">
              <div className="flex justify-between">
                <div className="flex flex-col">
                  <h3 className="font-semibold mr-1"> Amount You Pay </h3>
                  <div className='text-xs text-gray-500'>Principal + Interest</div>
                </div>
                <div className="text-right">
                  <div className="font-bold">
                    {formatCurrency(amountYouPay)}
                  </div>
                </div>
              </div>
            </div>

            {/* Fixed Fee (hidden by default) */}
            {hideMe &&
              (<div className="mb-3 p-2 bg-gray-50 rounded-lg">
                <div className="flex justify-between items-center">
                  <div className="flex items-center">
                    <h3 className="font-semibold mr-1">Fixed Fee</h3>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-5 w-5 p-0"
                          onClick={handleToggle}>
                          <Info className="h-3 w-3 text-gray-500" />
                          <span className="sr-only">Fee details</span>
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="text-xs">One-time fee based on loan tenure</p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  <div className="text-right">
                    <div className="font-bold">
                      {formatCurrency(calculateFeeAmount())}
                    </div>
                    <div className="text-xs text-gray-500">
                      ({getFeePercentage()}%)
                    </div>
                  </div>
                </div>
              </div>)}

            {/* Repayment Method */}
            <div className="mb-4">
              <h3 className="font-semibold mb-2">Repayment Method</h3>
              <div className="grid grid-cols-1 gap-2">
                {/* Daily Installment */}
                <div
                  className={cn(
                    "border rounded-lg p-2 cursor-pointer transition-all",
                    repaymentMethod === 'fixed'
                      ? "border-intellend-green bg-green-50"
                      : "border-gray-200 hover:border-intellend-green"
                  )}
                  onClick={() => {
                    setRepaymentMethod('fixed');
                    getLoanSummaryAndSchedule({
                      loanAmount,
                      selectedTenure,
                      repaymentMethod: 'fixed',
                    });
                  }}
                >
                  <div className="flex items-center">
                    <div className={cn(
                      "w-4 h-4 rounded-full border-2 mr-2 flex items-center justify-center",
                      repaymentMethod === 'fixed' ? "border-intellend-green" : "border-gray-300"
                    )}>
                      {repaymentMethod === 'fixed' && (
                        <div className="w-2 h-2 rounded-full bg-intellend-green" />
                      )}
                    </div>
                    <h4 className="font-medium text-sm">{repayType === 'EDI' ? 'Daily' : 'Monthly'} Installment</h4>
                  </div>
                  <div className="ml-6 text-xs text-gray-600 mb-1">
                    Automatic deduction every day from your connected bank account
                  </div>
                  <div className="ml-6 bg-gray-100 p-1 rounded text-center font-semibold text-sm">
                    {formatCurrency(calculateDailyPayment())} / {repayType === 'EDI' ? 'day' : 'month'}
                  </div>
                </div>

                {/* Percentage of Daily Revenue Option (hidden) */}
                {false &&
                  <div
                    className={cn(
                      "border rounded-lg p-2 cursor-pointer transition-all",
                      repaymentMethod === 'percentage'
                        ? "border-intellend-green bg-green-50"
                        : "border-gray-200 hover:border-intellend-green"
                    )}
                    onClick={() => {
                      setRepaymentMethod('percentage');
                      getLoanSummaryAndSchedule({
                        loanAmount,
                        selectedTenure,
                        repaymentMethod: 'percentage',
                      });
                    }}
                  >
                    <div className="flex items-center">
                      <div className={cn(
                        "w-4 h-4 rounded-full border-2 mr-2 flex items-center justify-center",
                        repaymentMethod === 'percentage' ? "border-intellend-green" : "border-gray-300"
                      )}>
                        {repaymentMethod === 'percentage' && (
                          <div className="w-2 h-2 rounded-full bg-intellend-green" />
                        )}
                      </div>
                      <h4 className="font-medium text-sm">% of Daily Revenue</h4>
                    </div>
                    <div className="ml-6 text-xs text-gray-600 mb-1">
                      Pay a percentage of your daily revenue
                    </div>
                    <div className="ml-6 bg-gray-100 p-1 rounded text-center font-semibold text-sm">
                      {calculateDailyRevenuePercentage().toFixed(1)}% of daily revenue
                    </div>
                  </div>}
              </div>
            </div>

            {/* Loan Tenure */}
            <div className="mb-3">
              <h3 className="font-semibold mb-2">Loan Tenure</h3>
              <div className='flex flex-row flex-nowrap justify-around items-stretch content-stretch gap-2'>
                {dynamicTenureOptions.map((option) => (
                  <div
                    key={option.months}
                    className='block flex-grow self-auto'>
                    <Button
                      onClick={() => {
                        setSelectedTenure(option.months);
                        getLoanSummaryAndSchedule({
                          loanAmount,
                          selectedTenure: option.months,
                          repaymentMethod,
                        });
                      }}
                      variant={option.months === selectedTenure ? "default" : "outline"}
                      className={cn(
                        option.months === selectedTenure ? "bg-intellend-green hover:bg-intellend-green/90" : "",
                        "w-full text-sm py-1 h-auto"
                      )}
                    >
                      {option.label}
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            {/* Provisional KFS Disclaimer and Link */}
            <div className="my-6">
              <p className="text-xs text-gray-500 italic mb-4">
                * All values, figures, and illustrations provided in this application are tentative and subject to verification, approval, and change without prior notice. Final loan terms, interest rates, fees, and repayment schedules will be confirmed only after completion of the evaluation and sanction process by the lender. Final (net) amount disbursed to your account may be different due to deduction of broken period interest, based on the exact disbursement date of your loan.
              </p>
              {offerId && (
                <div className="flex justify-center">
                  <a
                    href={`/kfs/${offerId}?amount=${loanAmount}&tenure=${selectedTenure}&method=${repaymentMethod}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-intellend-green font-semibold hover:underline flex items-center gap-1"
                  >
                    + View Detailed Repayment Schedule & Provisional KFS
                  </a>
                </div>
              )}
            </div>
          </div>{/* end blurred wrapper */}

          {/* Action Buttons — always visible, but Next is disabled while data is stale or loading */}
          <div className="flex gap-2 mt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsCallbackOpen(true)}
              className="text-sm py-1 h-auto"
            >
              Request Callback
            </Button>
            <Button
              className="ml-auto bg-intellend-green hover:bg-intellend-green/90 text-sm py-1 h-auto"
              onClick={handleNext}
              disabled={!isFormSynced || isDummyData || isDataStale}
            >
              {(!isFormSynced || isDummyData) ? "Loading..." : isDataStale ? "Calculate first" : "Next"}
            </Button>
          </div>
        </div>
      </main>

      {/* Callback Dialog */}
      <CallbackHelp
        isOpen={isCallbackOpen}
        setIsOpen={setIsCallbackOpen}
        showOk={false}
        showCallUs={true}
        showWhatsappUs={true}
        silentlyTrigger={true}
        title="Intellend Support"
      />
    </div>
  );
};

export default LoanOffer;
